import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles, HelpCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { streamChat } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SUGGESTIONS = [
  "What can WebsiteTech do for my business?",
  "How does the free website audit work?",
  "How much does an AI website cost?",
  "How does AI website automation work?",
  "Can you make my website multilingual?",
  "How do I improve my SEO?",
];

const GREETING = "Hi — I'm WebsiteTech AI. Ask me anything about building, modernizing or growing your website, or run a free analysis above.";

const mdComponents = {
  h1: ({ node, ...p }) => <h4 className="font-heading font-bold text-slate-900 text-sm mt-2 mb-1" {...p} />,
  h2: ({ node, ...p }) => <h4 className="font-heading font-bold text-slate-900 text-sm mt-2 mb-1" {...p} />,
  h3: ({ node, ...p }) => <h4 className="font-heading font-bold text-slate-900 text-sm mt-2 mb-1" {...p} />,
  p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-4 space-y-1 mb-2" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-4 space-y-1 mb-2" {...p} />,
  li: ({ node, ...p }) => <li {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-slate-900" {...p} />,
  a: ({ node, ...p }) => <a className="text-brand underline" target="_blank" rel="noopener noreferrer" {...p} />,
  code: ({ node, ...p }) => <code className="bg-slate-100 rounded px-1 py-0.5 text-[12px] font-mono" {...p} />,
};

let _mid = 0;
const newId = () => `m${++_mid}-${Math.random().toString(36).slice(2, 6)}`;

export default function AIAssistant() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `sess-${Math.random().toString(36).slice(2)}`);
  const [messages, setMessages] = useState([{ id: "greeting", role: "assistant", content: GREETING, greeting: true }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, suggestions]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-ai", openHandler);
    return () => window.removeEventListener("open-ai", openHandler);
  }, []);

  // Load dynamic suggestions (curated + most-asked visitor questions).
  useEffect(() => {
    fetch(`${BACKEND}/api/faq/suggestions?limit=6`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.suggestions?.length) setSuggestions(d.suggestions); })
      .catch(() => {});
  }, []);

  const captureQA = (question, answer) => {
    if (!answer || answer.length < 2) return;
    fetch(`${BACKEND}/api/faq/capture`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question, answer }),
    }).catch(() => {});
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    const answerId = newId();
    setMessages((m) => [...m, { id: newId(), role: "user", content: msg }, { id: answerId, role: "assistant", content: "" }]);
    setStreaming(true);
    let finalAnswer = "";
    try {
      await streamChat(
        sessionId,
        msg,
        (delta) => {
          finalAnswer += delta;
          setMessages((m) => m.map((x) => (x.id === answerId ? { ...x, content: x.content + delta } : x)));
        },
        () => { setStreaming(false); captureQA(msg, finalAnswer); }
      );
    } catch {
      setMessages((m) => m.map((x) => (x.id === answerId ? { ...x, content: "Sorry, I had trouble connecting. Please try again." } : x)));
      setStreaming(false);
    }
  };

  // Close an answer: remove the answer bubble and its preceding question, then suggestions remain.
  const closeAnswer = (answerId) => {
    setMessages((m) => {
      const idx = m.findIndex((x) => x.id === answerId);
      if (idx < 0) return m;
      const removeIds = new Set([answerId]);
      if (idx > 0 && m[idx - 1].role === "user") removeIds.add(m[idx - 1].id);
      return m.filter((x) => !removeIds.has(x.id));
    });
  };

  const lastId = messages[messages.length - 1]?.id;

  // Hide the site-wide bubble on pages that embed their own assistant or are client-facing dashboards.
  if (pathname.startsWith("/assistant/") || pathname.startsWith("/foodpulse") || pathname.startsWith("/dashboard/") || pathname.startsWith("/ai-chatbot") || pathname.startsWith("/in") || pathname.startsWith("/kerala") || pathname.startsWith("/demo") || pathname.startsWith("/litlab") || pathname.startsWith("/launch")) return null;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-assistant-toggle"
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-brand text-white flex items-center justify-center soft-shadow-lg hover:scale-105 transition-transform"
        aria-label="Talk to WebsiteTech AI"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[540px] max-h-[75vh] bg-white rounded-2xl border border-slate-200 soft-shadow-lg flex flex-col overflow-hidden"
            data-testid="ai-assistant-panel"
            data-no-translate
          >
            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
              <Sparkles size={16} className="text-brand" />
              <span className="font-heading font-bold text-slate-900">WebsiteTech AI</span>
              <span className="ml-auto text-[10px] font-semibold text-green-600 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> online
              </span>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50/50">
              {messages.map((m) => {
                const isUser = m.role === "user";
                const isStreamingThis = streaming && m.id === lastId && m.role === "assistant";
                const canClose = m.role === "assistant" && !m.greeting && !isStreamingThis;
                return (
                  <div key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                    <div className={`relative max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${isUser ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                      {canClose && (
                        <button
                          onClick={() => closeAnswer(m.id)}
                          data-testid={`answer-close-${m.id}`}
                          aria-label="Close answer"
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 flex items-center justify-center shadow-sm transition-colors"
                        >
                          <X size={13} />
                        </button>
                      )}
                      {m.content ? (
                        isUser ? m.content : <div className="markdown-body"><ReactMarkdown components={mdComponents}>{m.content}</ReactMarkdown></div>
                      ) : (
                        <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Suggested questions — always reachable */}
              {!streaming && suggestions.length > 0 && (
                <div className="pt-1">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                    <HelpCircle size={12} /> Suggested questions
                  </div>
                  <div className="space-y-2">
                    {suggestions.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="block w-full text-left text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-brand hover:text-brand transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-100 flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask WebsiteTech AI…"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand transition-colors placeholder:text-slate-400"
              />
              <button onClick={() => send()} disabled={streaming} data-testid="ai-chat-send" className="btn-primary rounded-lg w-10 h-10 flex items-center justify-center disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const Dot = ({ d = 0 }) => (
  <motion.span
    className="w-1.5 h-1.5 bg-slate-300 rounded-full"
    animate={{ opacity: [0.3, 1, 0.3] }}
    transition={{ duration: 1, repeat: Infinity, delay: d }}
  />
);
